import { Box, Button, Stack, Tab, Tabs } from '@mui/material';
import React, { useState } from 'react';
import type { RegistryDocument } from '../../data/types';
import DocumentViewerDialog from '../../documents/DocumentViewerDialog';
import { Navigate, useParams } from 'react-router-dom';
import { compactMoney, date, DEMO_TODAY } from '../../lib/format';
import { HOLDER_TYPE_LABELS } from '../../lib/labels';
import { sumBy, useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { PageHeader, StatCard, StatGrid, StatusChip } from '../../ui/primitives';
import HoldingsTable from '../common/HoldingsTable';
import LedgerTable from '../common/LedgerTable';
import HolderActivityTab from './HolderActivityTab';
import HolderComplianceTab from './HolderComplianceTab';
import HolderDocumentsTab from './HolderDocumentsTab';
import HolderOverviewTab from './HolderOverviewTab';
import HolderPaymentsTab from './HolderPaymentsTab';

export default function HolderDetailPage() {
  const { holderId } = useParams();
  const scope = useTenantScope();
  const toast = useToast();
  const [tab, setTab] = useState(0);
  const [viewing, setViewing] = useState<RegistryDocument | null>(null);
  const holder = holderId ? scope.holderById.get(holderId) : undefined;
  if (!holder) return <Navigate to="/manager/holders/sample" replace />;

  const holdings = scope.holdings.filter((holding) => holding.holderId === holder.id);
  const entries = scope.ledger.filter((entry) => entry.holderId === holder.id);
  const income = sumBy(
    entries.filter((entry) => entry.type === 'DISTRIBUTION' && entry.effectiveDate >= '2026-07-01'),
    (entry) => entry.amount,
  );
  const withheld = sumBy(
    entries.filter((entry) => entry.type === 'WITHHOLDING' && entry.effectiveDate >= '2026-07-01'),
    (entry) => entry.amount,
  );

  return (
    <>
      <PageHeader
        eyebrow={`${holder.accountNumber} · ${HOLDER_TYPE_LABELS[holder.type]} · holder since ${date(holder.createdAt)}`}
        title={holder.name}
        subtitle={
          <Stack
            useFlexGap
            direction="row"
            spacing={1}
            sx={{ flexWrap: 'wrap', rowGap: 1, mt: 0.5 }}
            component="span"
          >
            <StatusChip status={holder.kycStatus} label={`KYC ${holder.kycStatus.toLowerCase()}`} />
            <StatusChip status={holder.tfnStatus} />
            <StatusChip
              status={holder.riskRating}
              label={`${holder.riskRating.toLowerCase()} risk`}
            />
            {holder.pep && <StatusChip status="HIGH" label="PEP" />}
            {holder.residency !== 'AU' && (
              <StatusChip status="REPORTABLE" label={`Resident of ${holder.residency}`} />
            )}
            {holder.investorLoginId && <StatusChip status="OPEN" label="Investor app login" />}
          </Stack>
        }
        actions={
          <>
            <Button
              variant="outlined"
              onClick={() =>
                setViewing({
                  id: `hs-${holder.id}`,
                  holderId: holder.id,
                  kind: 'HOLDING_STATEMENT',
                  name: 'Holding statement (today)',
                  date: DEMO_TODAY,
                  sizeKb: 0,
                  visibleToInvestor: true,
                })
              }
            >
              Holding statement
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                toast(
                  'Secure message composer would open here (uses existing notifications)',
                  'info',
                )
              }
            >
              Message holder
            </Button>
          </>
        }
      />
      <StatGrid>
        <StatCard
          label="Value held"
          value={compactMoney(sumBy(holdings, (holding) => holding.value))}
          hint={`${holdings.length} holdings`}
        />
        <StatCard
          label="Uncalled commitment"
          value={compactMoney(sumBy(holdings, (holding) => holding.uncalled ?? 0))}
        />
        <StatCard label="Net income FY2027" value={compactMoney(income)} />
        <StatCard
          label="Tax withheld FY2027"
          value={compactMoney(withheld)}
          tone={withheld > 0 ? 'warning' : 'neutral'}
        />
      </StatGrid>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_event, next) => setTab(next)} variant="scrollable">
          <Tab label="Overview" />
          <Tab label={`Holdings (${holdings.length})`} />
          <Tab label={`Transactions (${entries.length})`} />
          <Tab label="Compliance" />
          <Tab label="Payments & tax" />
          <Tab label="Documents" />
          <Tab label="Activity" />
        </Tabs>
      </Box>
      {tab === 0 && <HolderOverviewTab holder={holder} />}
      {tab === 1 && <HoldingsTable holdings={holdings} showHolder={false} />}
      {tab === 2 && <LedgerTable entries={entries} showHolder={false} />}
      {tab === 3 && <HolderComplianceTab holder={holder} />}
      {tab === 4 && <HolderPaymentsTab holder={holder} />}
      {tab === 5 && <HolderDocumentsTab holder={holder} />}
      {tab === 6 && <HolderActivityTab holder={holder} />}
      <DocumentViewerDialog doc={viewing} onClose={() => setViewing(null)} />
    </>
  );
}
